import React, { useMemo, useState } from 'react';
import { createAttempt, submitReading } from '../../api/exams';
import { setPartProgress, computeMistakes, scoreReadingPart } from './_progress';
import { T } from './_theme';
import {
  ReadingFrame, PartTopBar, FeedbackCard, PrimaryBtn, GhostBtn, NudgeBanner,
  readArabicPref, readingHomeHref,
} from './_primitives';

// FET Reading — Part 3 · Text Matching
//
// Content shape:
//   { people: [{ id, name, color, text }, ...],
//     questions: [{ n, text, correct, why_en, why_ar, mistake_en, mistake_ar,
//                   evidence, examples }] }
// Submission key per question: q_{i} → person id

export default function Part3Screen({
  isMobile, navigate, exam, loading,
  onContinueToNextPart, nextPartLabel,
}) {
  const part = useMemo(
    () => (exam?.reading_parts || []).find((p) => p.part_number === 3),
    [exam],
  );
  const people = useMemo(() => (part?.content?.people || []).filter(Boolean), [part]);
  const questions = useMemo(() => (part?.content?.questions || []).filter(Boolean), [part]);
  const total = questions.length;

  const [arabicOn, setArabicOn] = useState(readArabicPref);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [activeTab, setActiveTab] = useState(people[0]?.id || '');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState('');

  const persistArabic = (next) => {
    setArabicOn(next);
    try { window.localStorage.setItem('fet_reading_arabic', next ? '1' : '0'); } catch {}
  };

  if (loading || !exam) {
    return <ReadingFrame isMobile={isMobile}><div style={{ padding: 32, color: T.ink2, fontFamily: T.sans }}>Loading reading exam…</div></ReadingFrame>;
  }
  if (!part || !total || !people.length) {
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={3} partTitle="Text Matching" currentIdx={0} total={1} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24, color: T.ink2, fontFamily: T.sans, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: T.ink }}>Part 3 isn't ready</div>
          <div>An admin needs to add at least one person text and one matching question.</div>
        </div>
      </ReadingFrame>
    );
  }

  if (finalResult) {
    const pct = finalResult.max ? Math.round((finalResult.score / finalResult.max) * 100) : 0;
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={3} partTitle="Text Matching" currentIdx={total - 1} total={total} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.hairline}`, borderRadius: 18, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: T.ink3, marginBottom: 12 }}>Part 3 result</div>
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 48, fontWeight: 700, color: T.blue, fontVariantNumeric: 'tabular-nums' }}>{finalResult.score}</span>
              <span style={{ fontSize: 20, color: T.ink3, fontWeight: 600 }}>/ {finalResult.max}</span>
            </div>
            <div style={{ fontSize: 14, color: T.ink2, marginTop: 8 }}>You scored <strong style={{ color: T.ink }}>{pct}%</strong>.</div>
            <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <GhostBtn onClick={() => { setAnswers({}); setCurrentIdx(0); setFinalResult(null); setError(''); }}>Try again</GhostBtn>
              {onContinueToNextPart ? (
                <PrimaryBtn full={false} onClick={onContinueToNextPart}>{nextPartLabel || 'Next part →'}</PrimaryBtn>
              ) : (
                <PrimaryBtn full={false} onClick={() => navigate('/dashboard')}>Back to dashboard</PrimaryBtn>
              )}
            </div>
          </div>
        </div>
      </ReadingFrame>
    );
  }

  const q = questions[currentIdx] || {};
  const picked = answers[`q_${currentIdx}`] || null;
  const answered = picked !== null;
  const correctId = String(q.correct || '').toLowerCase();
  const isCorrect = answered && picked.toLowerCase() === correctId;
  const isLast = currentIdx === total - 1;
  const activePerson = people.find((p) => p.id === activeTab) || people[0];
  const correctPerson = people.find((p) => p.id === correctId);

  const pickAnswer = (id) => {
    if (answered) return;
    setAnswers((prev) => ({ ...prev, [`q_${currentIdx}`]: id }));
  };

  const goNext = async () => {
    if (!isLast) {
      setCurrentIdx((i) => Math.min(total - 1, i + 1));
      return;
    }
    setSubmitting(true);
    setError('');
    const local = scoreReadingPart(3, part?.content, answers);
    const result = {
      score: local.score,
      max: local.max || total,
      mistakes: computeMistakes(3, part?.content, answers),
    };
    setFinalResult(result);
    setPartProgress(3, result);
    try {
      const attempt = await createAttempt(exam.id, 'practice', 'reading');
      await submitReading(attempt.id, { '3': answers });
    } catch (err) {
      const apiErr = err?.response?.data;
      setError(apiErr?.error || 'Could not save your attempt to the server. Your local score is shown.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ReadingFrame isMobile={isMobile}>
      <PartTopBar partNumber={3} partTitle="Text Matching" currentIdx={currentIdx} total={total} onBack={() => navigate(readingHomeHref())} />
      <div style={{ flex: 1, padding: 16 }}>
        <div style={{ padding: '12px 14px', background: T.surface, borderRadius: 14, border: `1px solid ${T.hairline}`, marginBottom: 12 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: T.ink3, fontWeight: 700, marginBottom: 4 }}>Question</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{q.text}</div>
        </div>

        {/* People tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {people.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActiveTab(p.id)}
              style={{
                flex: 1, minWidth: 80,
                padding: '9px 6px', borderRadius: 14,
                fontSize: 13, fontWeight: 700, textAlign: 'center',
                background: activeTab === p.id ? (p.color || T.p3) : T.surface,
                color: activeTab === p.id ? '#fff' : T.ink2,
                border: `1.5px solid ${activeTab === p.id ? (p.color || T.p3) : T.hairline}`,
                cursor: 'pointer', fontFamily: T.sans,
              }}
            >{p.name}</button>
          ))}
        </div>

        {/* Active person's text */}
        {activePerson && (
          <div style={{
            padding: '14px 16px', background: T.surface, borderRadius: 14,
            border: `1px solid ${T.hairline}`,
            borderLeft: `4px solid ${activePerson.color || T.p3}`,
            fontSize: 14, lineHeight: 1.7, fontFamily: T.serif, color: T.ink,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: activePerson.color || T.p3, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 6, fontFamily: T.sans }}>
              {activePerson.name}
            </div>
            {activePerson.text}
          </div>
        )}

        {/* Answer chips */}
        <div style={{ marginTop: 18, fontSize: 12, color: T.ink3, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Your answer</div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(people.length, 3)}, 1fr)`, gap: 8 }}>
          {people.map((p) => {
            const isPicked = picked === p.id;
            const isThisCorrect = answered && p.id === correctId;
            const isThisWrongPick = answered && isPicked && !isThisCorrect;
            const bg = isThisCorrect ? T.okBg : isThisWrongPick ? T.missBg : T.surface;
            const bd = isThisCorrect ? T.ok : isThisWrongPick ? T.miss : (isPicked ? T.blue : T.hairlineInk);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => pickAnswer(p.id)}
                disabled={answered}
                style={{
                  padding: '14px 8px',
                  background: bg,
                  border: `1.5px solid ${bd}`,
                  borderRadius: 14,
                  fontSize: 13, fontWeight: 700, textAlign: 'center',
                  opacity: answered && !isThisCorrect && !isPicked ? 0.65 : 1,
                  cursor: answered ? 'default' : 'pointer',
                  fontFamily: T.sans, color: T.ink,
                }}
              >
                <div style={{ width: 8, height: 8, borderRadius: 4, background: p.color || T.ink2, margin: '0 auto 6px' }} />
                {p.name}
              </button>
            );
          })}
        </div>

        {answered && (
          <FeedbackCard
            correct={isCorrect}
            header={isCorrect ? `Correct — ${correctPerson?.name || 'right person'}` : `Almost — it's ${correctPerson?.name || 'someone else'}`}
            picked={people.find((p) => p.id === picked)?.name || picked}
            lang={arabicOn ? 'ar' : 'en'}
            onLangChange={(v) => persistArabic(v === 'ar')}
            simple={arabicOn ? q.why_ar : q.why_en}
            mistake={!isCorrect ? (arabicOn ? q.mistake_ar : q.mistake_en) : null}
            examples={Array.isArray(q.examples) ? q.examples : null}
            evidence={q.evidence}
          />
        )}

        {error && <div style={{ marginTop: 12, fontSize: 13, color: T.miss }}>{error}</div>}
        {!answered && <div style={{ marginTop: 14 }}><NudgeBanner>Pick the person who matches the question to continue.</NudgeBanner></div>}
      </div>

      <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.hairline}`, background: T.surface, display: 'flex', gap: 10 }}>
        <GhostBtn disabled={currentIdx === 0} onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}>← Previous</GhostBtn>
        <div style={{ flex: 1 }} />
        <PrimaryBtn full={false} disabled={!answered || submitting} onClick={goNext}>
          {submitting ? 'Saving…' : isLast ? 'Finish part →' : 'Next question →'}
        </PrimaryBtn>
      </div>
    </ReadingFrame>
  );
}
